import React from 'react';
import Section from '../Section';

import {
  FaUserShield, FaInfoCircle, FaFileAlt, FaLock, FaRegClock,
  FaUserCheck, FaCookieBite, FaMobileAlt, FaMoneyBillWave,
  FaSyncAlt, FaEnvelope, FaGavel, FaCopyright
} from 'react-icons/fa';
import { pageTitle } from '../../helpers/PageTitle';

const iconStyle = { color: '#aad1f0', marginRight: '8px' };

const SectionCard = ({ icon, title, children }) => (
  <div className="card shadow-lg p-4 mb-4 border-0 rounded-4">
    <h4 className="mb-3 d-flex align-items-center">{icon} {title}</h4>
    {children}
  </div>
);

const TermsConditions = () => {
  pageTitle("Terms & Conditions")
  return (
    <Section
      topMd={185}
      topLg={140}
      topXl={100}
      bottomMd={200}
      bottomLg={150}
      bottomXl={110}
    >
      <div className="container py-5">
        <h1 className="mb-5 text-center"style={{marginTop:"70px"}}>Terms & Conditions</h1>

        <SectionCard icon={<FaInfoCircle style={iconStyle} />} title="Introduction">
          <p>These Terms & Conditions (“Terms”) govern your use of the GKC Klinica website (www.gkcklinica.com) and mobile application (together referred to as the “Platform”), operated by GKC Klinica Pvt. Ltd. (“we,” “us,” or “our”).</p>
          <p>By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree, please do not use the Platform.</p>
        </SectionCard>

        <SectionCard icon={<FaUserCheck style={iconStyle} />} title="Eligibility">
          <ul>
            <li>You must be at least 18 years old to use our Platform.</li>
            <li>You are legally competent and authorized to enter into a binding agreement.</li>
            <li>The information you provide is accurate, current, and complete.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaMobileAlt style={iconStyle} />} title="Services Offered">
          <p>GKC Klinica offers the following services through the Platform:</p>
          <ul>
            <li>Booking and management of health checkups and diagnostic tests</li>
            <li>Video/audio consultations with licensed medical professionals</li>
            <li>Uploading, managing, and sharing insurance policy documents</li>
            <li>Purchasing and managing GKC Klinica membership plans</li>
            <li>Access to personal medical records, diagnostic reports, and health history</li>
            <li>Health reminders, wellness notifications, and promotional offers</li>
            <li>We reserve the right to modify or discontinue any service without prior notice.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaFileAlt style={iconStyle} />} title="Terms of Service: Merchant–Consumer Transactions">
          <h6>Scope</h6>
          <p>This section governs the relationship between GKC Klinica Pvt. Ltd. (as the Merchant) and users (Consumers) who purchase any paid service or product through the Platform.</p>
          <h6>Products and Services</h6>
          <p>Products and services sold or facilitated via the Platform include but are not limited to:</p>
          <ul>
            <li>Preventive and full-body health checkups</li>
            <li>Lab diagnostics and radiology tests</li>
            <li>Online consultations with doctors</li>
            <li>Membership and subscription plans offering bundled services</li>
            <li>Value-added services such as insurance document management and appointment coordination</li>
          </ul>
          <h6>Order Placement and Confirmation</h6>
          <ul>
            <li>Orders/bookings can be placed through the Platform interface by registered users.</li>
            <li>After placement, you will receive a confirmation via email or SMS with booking details.</li>
            <li>GKC Klinica reserves the right to accept or reject any order due to availability, service area constraints, or user history.</li>
          </ul>
          <h6>Service Fulfillment</h6>
          <ul>
            <li>Services are fulfilled either by GKC Klinica or its authorized partner clinics/labs.</li>
            <li>Consultations are delivered via secure video/audio call features.</li>
            <li>Test reports will be made available digitally via your account.</li>
            <li>Memberships, once activated, provide access to benefits for a defined period.</li>
          </ul>
          <h6>Pricing and Payment</h6>
          <ul>
            <li>All prices listed on the Platform are in Indian Rupees (INR) and inclusive of applicable taxes unless specified.</li>
            <li>Prices for checkups, consultations, and memberships are dynamic and subject to change.</li>
            <li>Payment must be made through the Platform using accepted payment gateways (credit/debit cards, UPI, net banking, wallets).</li>
            <li>GKC Klinica is not responsible for any delay or failure in payment processing by third-party gateways.</li>
          </ul>
          <h6>Cancellations and Refunds</h6>
          <ul>
            <li>Users may request cancellations for services based on the conditions outlined in our Refund Policy.</li>
            <li>Refunds, where applicable, will be processed to the original payment method within a reasonable time.</li>
            <li>Memberships and completed consultations are non-refundable unless otherwise stated.</li>
          </ul>
          <h6>Customer Support</h6>
          <p>For service-related issues, users can contact our support team at support@gkcklinica.com or call +91 97668 33510 (Monday–Friday, 9:00 AM – 5:00 PM IST).</p>
          <p>Disputes or grievances will be addressed in accordance with Indian consumer protection laws.</p>
        </SectionCard>

        <SectionCard icon={<FaUserShield style={iconStyle} />} title="User Responsibilities">
          <ul>
            <li>Use the Platform only for lawful and personal use.</li>
            <li>Maintain the confidentiality of your login credentials.</li>
            <li>Do not impersonate or misrepresent identity.</li>
            <li>Do not introduce harmful code or misuse the Platform.</li>
            <li>Do not engage in scraping or automated access of services.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaRegClock style={iconStyle} />} title="Health Disclaimer">
          <ul>
            <li>GKC Klinica is not an emergency service. In an emergency, please call 108 or visit a hospital.</li>
            <li>Platform-based consultations are intended for non-urgent health issues and do not replace physical examinations.</li>
            <li>Always follow medical advice from your primary physician or specialist.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaMoneyBillWave style={iconStyle} />} title="Payments and Memberships">
          <ul>
            <li>Payments must be made only through authorized payment options on the Platform.</li>
            <li>All memberships are non-transferable, non-refundable (unless otherwise specified), and governed by specific plan terms.</li>
            <li>GKC Klinica reserves the right to revise prices or discontinue plans with prior notice.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaFileAlt style={iconStyle} />} title="Insurance Document Handling">
          <ul>
            <li>You are authorized to upload such documents.</li>
            <li>You consent to share these documents with chosen clinics, labs, or insurance partners for processing.</li>
            <li>GKC Klinica does not act as an insurer or underwrite any insurance.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaLock style={iconStyle} />} title="Medical Records and Data Use">
          <ul>
            <li>Your data is stored securely and accessible only by you and authorized parties.</li>
            <li>You may share data with doctors, labs, or insurers.</li>
            <li>We follow applicable Indian data protection laws. Refer to our Privacy Policies.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaSyncAlt style={iconStyle} />} title="Account Suspension or Termination">
          <ul>
            <li>We may suspend or terminate your account for:
              <ul>
                <li>Violation of these Terms</li>
                <li>Fraudulent activity or misuse</li>
                <li>Regulatory or legal requirements</li>
              </ul>
            </li>
            <li>You may also request account deletion by writing to support@gkcklinica.com.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaCopyright style={iconStyle} />} title="Intellectual Property">
          <p>All Platform content (logos, UI, text, software) is the property of GKC Klinica or licensors. You may not reuse without permission.</p>
        </SectionCard>

        <SectionCard icon={<FaCookieBite style={iconStyle} />} title="Third-Party Services">
          <p>The Platform may integrate with services such as:</p>
          <ul>
            <li>Diagnostic labs</li>
            <li>Payment gateways</li>
            <li>Insurance comparison tools</li>
          </ul>
          <p>We are not liable for third-party errors, accuracy, or service disruptions. Use at your own discretion.</p>
        </SectionCard>

        <SectionCard icon={<FaInfoCircle style={iconStyle} />} title="Disclaimers and Limitation of Liability">
          <ul>
            <li>The Platform is provided “as is” and “as available.”</li>
            <li>We make no guarantees regarding uninterrupted access, accuracy, or service outcomes.</li>
            <li>GKC Klinica shall not be liable for any indirect, incidental, or consequential losses.</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaUserShield style={iconStyle} />} title="Indemnity">
          <p>You agree to indemnify GKC Klinica from any legal claims arising from:</p>
          <ul>
            <li>Your use of the Platform</li>
            <li>Violation of Terms</li>
            <li>Infringement of third-party rights</li>
          </ul>
        </SectionCard>

        <SectionCard icon={<FaGavel style={iconStyle} />} title="Governing Law and Jurisdiction">
          <p>These Terms shall be governed by Indian laws. Jurisdiction shall lie exclusively with the competent courts in Pune, Maharashtra, unless otherwise specified.</p>
        </SectionCard>

        <SectionCard icon={<FaSyncAlt style={iconStyle} />} title="Changes to These Terms">
          <p>We may revise these Terms from time to time. Continued use after changes indicates acceptance of updated Terms. The latest version will always be available on our website.</p>
        </SectionCard>

        <SectionCard icon={<FaEnvelope style={iconStyle} />} title="Contact Us">
          <p>Email: support@gkcklinica.com</p>
          <p>Phone: +91 97668 33510 (Mon–Fri, 9:00 AM – 5:00 PM IST)</p>
        </SectionCard>
      </div>
    </Section>
  );
};

export default TermsConditions;
